import { useMemo } from 'react'
import { RoleType } from '../../models/Role'
import { useGetInvitesQuery } from '../../store/auth/auth'
import { selectAccountId } from '../../store/auth/authSlice'
import { useAppSelector } from '../../store/hooks'
import { useGetBillingCustomerFreeSeatsQuery } from '../../store/plan/plan'
import { REQUESTED_SEAT_GROUPS, type RequestedGroupKey, freeSeatsFor } from './requestedGroups'
import { type TeamQueryFilters, requestedGroupFilter, requestedSeatTypeCountFilter } from './teamQueries'
import { type InviteRow, toInviteRow } from './teamRows'
import { useRefreshSeats } from './useRefreshSeats'

export interface RequestedSeatGroupState {
  key: RequestedGroupKey
  label: string
  seatType: RoleType
  rows: InviteRow[]
  totalRows: number
  isFetching: boolean
  freeSeats?: number
  page: number
}

export interface UseRequestedSeatGroupsArgs extends TeamQueryFilters {
  pages: Record<RequestedGroupKey, number>
  perPage: number
}

export interface UseRequestedSeatGroupsResult {
  groups: RequestedSeatGroupState[]
  refreshSeats: () => void
}

export const useRequestedSeatGroups = ({ pages, perPage, namePattern, roleGroupNames }: UseRequestedSeatGroupsArgs): UseRequestedSeatGroupsResult => {
  const accountId = useAppSelector(selectAccountId)
  const refreshSeats = useRefreshSeats()
  const filters = useMemo<TeamQueryFilters>(() => ({ namePattern, roleGroupNames }), [namePattern, roleGroupNames])

  const { data: freeSeatsData } = useGetBillingCustomerFreeSeatsQuery({ accountId }, { refetchOnMountOrArgChange: true })

  const advisorFilter = useMemo(() => requestedGroupFilter(RoleType.SEAT, filters), [filters])
  const assistantFilter = useMemo(() => requestedGroupFilter(RoleType.SIDECAR, filters), [filters])

  const advisor = useGetInvitesQuery(
    { accountId, pageRequest: { page: pages.advisor - 1, size: perPage }, filter: advisorFilter ?? requestedSeatTypeCountFilter(RoleType.SEAT) },
    { skip: !advisorFilter, refetchOnMountOrArgChange: true },
  )

  const assistant = useGetInvitesQuery(
    { accountId, pageRequest: { page: pages.assistant - 1, size: perPage }, filter: assistantFilter ?? requestedSeatTypeCountFilter(RoleType.SIDECAR) },
    { skip: !assistantFilter, refetchOnMountOrArgChange: true },
  )

  const groups = useMemo<RequestedSeatGroupState[]>(() => {
    const queries = { advisor, assistant }
    const filtered = { advisor: advisorFilter, assistant: assistantFilter }
    return REQUESTED_SEAT_GROUPS.map((group) => {
      const query = queries[group.key]
      const excluded = !filtered[group.key]
      return {
        ...group,
        rows: excluded ? [] : (query.data?.data ?? []).map(toInviteRow),
        totalRows: excluded ? 0 : (query.data?.page.totalElements ?? 0),
        isFetching: !excluded && query.isFetching,
        freeSeats: freeSeatsFor(group.seatType, freeSeatsData?.data.seats),
        page: pages[group.key],
      }
    })
  }, [advisor, assistant, advisorFilter, assistantFilter, freeSeatsData, pages])

  return { groups, refreshSeats }
}
